import { IDisposable } from '@app/interfaces/disposable';

export class Subject<TSender, TArgs> implements ISubject<TSender, TArgs> {
    private callbacks: IObserver<TSender, TArgs>[] = [];

    subscribe(callback: IObserver<TSender, TArgs>): IDisposable | null {
        if (this.callbacks.indexOf(callback) !== -1) {
            return null;
        }

        this.callbacks.push(callback);

        return new ObservableSubscription(() => {
            const index = this.callbacks.indexOf(callback);

            if (index >= 0) {
                this.callbacks.splice(index, 1);
            }
        });
    }

    emit(source: TSender, args: TArgs): boolean {
        for (let callback of this.callbacks) {
            const result = callback(source, args);

            if (typeof result === "boolean" && !result) {
                return false;
            }
        }

        return true;
    }
}

class ObservableSubscription<TSender, TArgs> implements IDisposable {
    private disposed: boolean;

    constructor(private disposeCallback: () => void) {
        this.disposed = false;
    }

    dispose(): void {
        if (this.disposed) {
            return;
        }

        this.disposed = true;
        this.disposeCallback();
    }
}

export class BehaviorSubject<TSender, TArgs> extends Subject<TSender, TArgs> {
    private lastSource!: TSender;
    private lastArgs!: TArgs;
    private hasLast: boolean = false;

    subscribe(callback: IObserver<TSender, TArgs>): IDisposable | null {
        const result = super.subscribe(callback);

        if (result && this.hasLast) {
            callback(this.lastSource, this.lastArgs);
        }

        return result ? result : null;
    }

    emit(source: TSender, args: TArgs): boolean {
        this.lastSource = source;
        this.lastArgs = args;
        this.hasLast = true;

        return super.emit(source, args);
    }
}

export interface IObservable<TSender, TArgs> {
    subscribe(callback: IObserver<TSender, TArgs>): IDisposable | null;
}

export interface ISubject<TSender, TArgs> extends IObservable<TSender, TArgs> {
    emit(source: TSender, args: TArgs): boolean;
}

export interface IObserver<TSender, TArgs> {
    (source: TSender, args: TArgs): boolean | void;
}